import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { fileToBase64, getImageData, padImageToRatio } from "utils/logicWithFiles"
import { compressImageWithLoading } from "utils/imageCompression"
import { COMPRESSION_CONFIG } from "../../config/compressionConfig"
import { ImageIcon, Loader2, Trash2, Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface UploadImageCardProps {
  onChange: (src: string | null, fileBase64?: string) => void
  src?: string
  title: string
  required?: boolean
  widthToHeightRatio: number
}

const allowedFileTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"]

interface UploadImageCardData {
  height?: number
  name?: string
  src?: string
  type?: string
  width?: number
}

export const UploadImageCard = ({
  onChange,
  src,
  title,
  required,
  widthToHeightRatio,
}: UploadImageCardProps) => {
  const { t } = useTranslation()
  const [data, setData] = useState<UploadImageCardData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isCompressing, setIsCompressing] = useState(false)
  const [compressionProgress, setCompressionProgress] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [imgError, setImgError] = useState(false)

  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const processFile = useCallback(
    async (file: File) => {
      if (!allowedFileTypes.includes(file.type)) {
        setError(t("uploadImageCard.invalidFileType") + ": PNG, JPEG, JPG, WEBP.")
        return
      }

      setIsCompressing(true)
      setCompressionProgress(0)
      setError(null)

      try {
        let sourceFile = file
        try {
          const result = await compressImageWithLoading(
            file,
            (progress) => setCompressionProgress(progress),
            COMPRESSION_CONFIG
          )
          sourceFile = result.compressedFile
        } catch {
          // Compression failed — fall back to the original file.
        }

        const fileBase64 = await fileToBase64(sourceFile)
        // Any aspect ratio is accepted: pad (never crop) to the target ratio so the
        // stored asset renders correctly in the height-locked widget logo slot.
        const padded = await padImageToRatio(fileBase64, widthToHeightRatio)

        setImgError(false)
        setData({
          name: sourceFile.name,
          type: sourceFile.type.split("/")[1]?.toUpperCase() ?? "",
          width: padded.width,
          height: padded.height,
          src: padded.src,
        })
        onChange(padded.src, padded.src)
        setError(null)
      } catch {
        setError(t("uploadImageCard.invalidFileType") + ": PNG, JPEG, JPG, WEBP.")
      } finally {
        setIsCompressing(false)
        setCompressionProgress(0)
      }
    },
    [onChange, widthToHeightRatio, t]
  )

  const onCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) await processFile(file)
    e.target.value = ""
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) await processFile(file)
    },
    [processFile]
  )

  const handleRemoveImage = () => {
    setData(null)
    onChange("", "")
    setError(null)
  }

  useEffect(() => {
    setImgError(false)
    if (!src) return

    getImageData(src, (imageData) => {
      const fileName = src.split("/").slice(-1)[0]
      setData((prevData) => ({
        ...(prevData ?? {}),
        ...imageData,
        name: fileName,
        type: fileName.split(".")[1]?.toUpperCase() || "",
      }))
    })
  }, [src])

  const hasImage = (data?.src || src) && !imgError
  const ratioLabel = widthToHeightRatio === 1 ? "1:1" : `${widthToHeightRatio}:1`
  const blockMaxWidth = widthToHeightRatio === 1 ? "14rem" : undefined

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between" style={{ maxWidth: blockMaxWidth }}>
        <label className="text-sm font-medium">{title}</label>
        <span className="text-xs text-muted-foreground">Ratio {ratioLabel}</span>
      </div>

      {hasImage ? (
        /* Image preview */
        <div
          className="group relative w-full overflow-hidden rounded-lg border bg-muted/30"
          style={{ maxWidth: blockMaxWidth }}
        >
          <div
            className="flex w-full items-center justify-center p-4"
            style={{ aspectRatio: `${widthToHeightRatio} / 1` }}
          >
            <img
              src={data?.src || src}
              alt={title}
              onError={() => setImgError(true)}
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => hiddenFileInput.current?.click()}
              disabled={isCompressing}
            >
              {isCompressing ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Upload className="mr-1.5 size-3.5" />
              )}
              {isCompressing ? `${compressionProgress}%` : "Replace"}
            </Button>
            <Button variant="destructive" size="sm" onClick={handleRemoveImage}>
              <Trash2 className="mr-1.5 size-3.5" />
              Remove
            </Button>
          </div>
          {data?.name && (
            <div className="flex items-center justify-between border-t px-3 py-2">
              <span className="truncate text-xs text-muted-foreground">{data.name}</span>
              {data.width && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {data.width}&times;{data.height}
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Drop zone */
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => {
            if (!isCompressing) hiddenFileInput.current?.click()
          }}
          style={{ aspectRatio: `${widthToHeightRatio} / 1`, maxWidth: blockMaxWidth }}
          className={cn(
            "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors hover:border-primary/50 hover:bg-muted/50",
            isDragging && "border-primary bg-primary/5",
            isCompressing && "pointer-events-none opacity-60"
          )}
        >
          {isCompressing ? (
            <>
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t("uploadImageCard.compressing")} {compressionProgress}%
              </span>
            </>
          ) : (
            <>
              <ImageIcon className="size-8 text-muted-foreground/60" />
              <div>
                <p className="text-sm font-medium">Drop image here or click to upload</p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPEG, WebP &middot; {ratioLabel} ratio
                </p>
              </div>
            </>
          )}
        </div>
      )}

      <input
        ref={hiddenFileInput}
        accept={allowedFileTypes.join(", ")}
        onChange={onCapture}
        type="file"
        hidden
        multiple={false}
        disabled={isCompressing}
      />

      {error && <p className="text-xs text-destructive">{error}</p>}
      {required && !hasImage && (
        <p className="text-xs text-destructive">{`* ${t("uploadImageCard.required")}`}</p>
      )}
    </div>
  )
}
